@file:OptIn(ExperimentalJsExport::class)

package game

@JsExport
enum class GameStatus {
    MENU,
    GARAGE,
    SHOP,
    PLAYING,
    PAUSED,
    GAME_OVER,
    VICTORY
}

// 底盘类型(轻/中/重甲)
@JsExport
enum class ChassisType {
    LIGHT,
    MEDIUM,
    HEAVY
}

// 子弹/武器类型
@JsExport
enum class BulletType {
    STANDARD,  // 标准炮弹
    LASER,     // 激光(高速穿透)
    MISSILE,   // 导弹(高伤范围)
    HOMING,    // 追踪弹
    MELEE,     // 近战电击
    SHOTGUN,   // 霰弹(多弹片)
    PLASMA,    // 等离子弹(高伤慢速)
    GATLING,   // 加特林(超快射速)
    EMP,       // 电磁脉冲(眩晕范围)
    GRENADE,   // 榴弹炮(弧形弹道,落地范围爆炸)
    FLAME,     // 火焰炮(持续燃烧)
    // ===== 🚀 超级武器 =====
    OBLIVION   // 💀 湮灭之核: 全屏秒杀 + 黑洞特效 + 无限穿透
}

// 道具类型
@JsExport
enum class PowerupType {
    HEALTH,   // 回复生命
    SPEED,    // 加速(持续)
    SHIELD,   // 护盾(持续)
    RAPID,    // 射速提升(持续)
    MULTI     // 多重射击(持续)
}

// 玩家技能
@JsExport
enum class SkillType {
    DASH,     // 冲刺
    SHIELD,   // 护盾
    NUKE      // 核弹
}

/**
 * 通过名称获取枚举值（不区分大小写）
 * @param name 枚举名称，如 "STANDARD"、"LASER"
 * @return 枚举值，如果不存在则返回 null
 */
inline fun <reified T : Enum<T>> Enum<T>.get(name: String): T? {
    return try {
        enumValues<T>().find { it.name.equals(name, ignoreCase = true) }
    } catch (e: Exception) {
        null
    }
}

/**
 * 通过名称获取枚举值（带默认值）
 * @param name 枚举名称
 * @param defaultValue 找不到时返回的默认值
 */
inline fun <reified T : Enum<T>> Enum<T>.getOrDefault(name: String, defaultValue: T): T {
    return get(name) ?: defaultValue
}

/**
 * 安全获取枚举值（不抛异常）
 * @param name 枚举名称
 * @return 枚举值，不存在返回 null
 */
inline fun <reified T : Enum<T>> safeValueOf(name: String): T? {
    return try {
        enumValues<T>().find { it.name.equals(name, ignoreCase = true) }
    } catch (e: Exception) {
        null
    }
}

/**
 * 获取所有枚举值名称列表
 */
inline fun <reified T : Enum<T>> Enum<T>.names(): List<String> {
    return enumValues<T>().map { it.name }
}

@JsExport
fun getGameStatus(type: String): GameStatus {
    return GameStatus.valueOf(type);
}

@JsExport
fun getChassis(type: String): ChassisType {
    return ChassisType.valueOf(type);
}

@JsExport
fun getBulletType(type: String): BulletType {
    return BulletType.valueOf(type);
}

@JsExport
fun getPowerup(type: String): PowerupType {
    return PowerupType.valueOf(type);
}

@JsExport
fun getSkill(type: String): SkillType {
    return SkillType.valueOf(type);
}

// 玩家输入(每帧快照)
@JsExport
data class PlayerInput(
    val forward: Boolean = false,
    val backward: Boolean = false,
    val left: Boolean = false,
    val right: Boolean = false,
    val fire: Boolean = false,
    val jump: Boolean = false,
    val dash: Boolean = false,    // Q
    val shield: Boolean = false,  // E
    val nuke: Boolean = false,    // R
    val aimAngle: Double? = null  // 鼠标瞄准的炮塔目标角度
)

// 坦克
@JsExport
data class TankState(
    val id: Int,
    val x: Double,
    val z: Double,
    val y: Double,              // Y 轴高度(跳跃)
    val vy: Double,             // Y 轴垂直速度
    val angle: Double,          // 车身朝向(弧度)
    val turretAngle: Double,    // 炮塔朝向(弧度),独立于车身
    val hp: Int,
    val maxHp: Int,
    val energy: Double,          // 能量(0-100,随时间恢复)
    val maxEnergy: Double,       // 最大能量
    val isPlayer: Boolean,
    val alive: Boolean,
    val fireCooldown: Double,   // 剩余冷却时间
    val speed: Double,
    val chassis: ChassisType,   // 底盘类型(决定速度/血量/护甲)
    val armor: Int,             // 护甲减伤百分比
    val weapon: BulletType,     // 当前武器
    // ===== 状态效果 =====
    val stunTimer: Double = 0.0,  // EMP 眩晕剩余时间
    val burnTimer: Double = 0.0,  // 火焰燃烧剩余时间(持续扣血)
    // ===== 道具 buff(剩余时间)=====
    val shieldTimer: Double = 0.0,  // 护盾剩余
    val speedTimer: Double = 0.0,   // 加速剩余
    val rapidTimer: Double = 0.0,   // 射速提升剩余
    val multiTimer: Double = 0.0,   // 多重射击剩余
    // ===== 技能 =====
    val dashCooldown: Double = 0.0,   // 冲刺冷却剩余
    val dashTime: Double = 0.0,       // 冲刺剩余持续时间
    val shieldCooldown: Double = 0.0, // 护盾技能冷却剩余
    val nukeCooldown: Double = 0.0    // 核弹冷却剩余
)

// 子弹(含类型)
@JsExport
data class BulletState(
    val id: Int,
    val x: Double,
    val z: Double,
    val vx: Double,
    val vz: Double,
    val ownerId: Int,
    val damage: Int,
    val life: Double,           // 剩余存活时间
    val type: BulletType,       // 子弹类型
    val pierce: Int = 0         // 穿透次数(激光)
)

// 道具(地图上的拾取物)
@JsExport
data class PowerupState(
    val id: Int,
    val type: PowerupType,
    val x: Double,
    val z: Double,
    val life: Double            // 剩余存活时间,过期消失
)

// 激光光束(即时射线,从起点到终点的一条光柱)
@JsExport
data class LaserBeamState(
    val id: Int,
    val x0: Double, val z0: Double,   // 起点(炮口)
    val x1: Double, val z1: Double,   // 终点(命中障碍物/敌人/边界)
    val life: Double,                 // 剩余显示时间
    val ownerId: Int
)

// 武器特效(电击电弧 / EMP 冲击波等)
@JsExport
data class EffectState(
    val id: Int,
    val type: EffectType,
    val x: Double, val z: Double,
    val life: Double,                 // 剩余显示时间
    val radius: Double                // 效果半径
)

@JsExport
enum class EffectType {
    MELEE_SHOCK,  // 电击电弧
    EMP_BLAST     // EMP 冲击波
}

@JsExport
fun getEffectType(type: String): EffectType {
    return EffectType.valueOf(type);
}

// 伤害飘字(在目标位置短暂显示伤害数值)
@JsExport
data class DamageNumberState(
    val id: Int,
    val x: Double, val z: Double,
    val amount: Int,
    val isPlayer: Boolean,   // true=玩家造成(黄色), false=玩家受到(红色)
    val life: Double         // 剩余显示时间
)

// 调试开关
@JsExport
data class DebugState(
    val godMode: Boolean = false,   // 无敌
    val showDamage: Boolean = true, // 显示伤害飘字
    val panelOpen: Boolean = false  // 调试面板开关
)

// 障碍物(AABB)
@JsExport
data class ObstacleState(
    val id: Int,
    val x: Double,
    val z: Double,
    val w: Double,
    val h: Double
)

@JsExport
data class GameState(
    val status: GameStatus,
    val player: TankState,
    val enemies: List<TankState>,
    val bullets: List<BulletState>,
    val lasers: List<LaserBeamState>,
    val effects: List<EffectState>,
    val powerups: List<PowerupState>,
    val obstacles: List<ObstacleState>,
    val score: Int,
    val wave: Int,
    val kills: Int,
    val nextId: Int,
    val powerupTimer: Double,   // 距下次刷新道具的时间
    val incomeTimer: Double,    // 距下次被动收入的时间
    // ===== 金币 / 商店 =====
    val coins: Int,                          // 当前金币
    val ownedWeapons: List<BulletType>,      // 已购买武器
    val selectedWeapon: BulletType,          // 当前装备武器
    val ownedChassis: List<ChassisType>,     // 已解锁底盘
    val selectedChassis: ChassisType,        // 当前底盘
    // ===== 伤害飘字 / 调试 =====
    val damageNumbers: List<DamageNumberState>,
    val debug: DebugState,
    val message: String
)
